// API Client for PluvP Backend Worker
import { Operator, PackageItem, AirtimeDenomination, Wallet, Order, Deposit, Buyback, ReceivingAccount, RateSchedule, DashboardStats, DashboardFilter, DashboardTimeframe, AdminCustomerListResult, AdminCustomerDetailResult } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export class ApiClient {
  private userId: string = '';
  private userRole: string = '';
  private adminSecret: string = '';

  setCredentials(userId: string, role: string, adminSecret?: string) {
    this.userId = userId;
    this.userRole = role;
    if (adminSecret !== undefined) {
      this.adminSecret = adminSecret;
    }
  }

  async authTelegramMiniApp(initData: string): Promise<any> {
    const res = await fetch(`${API_BASE}/auth/telegram-miniapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    return res.json();
  }


  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.userId) {
      headers['X-User-Id'] = this.userId;
    }
    if (this.userRole) {
      headers['X-User-Role'] = this.userRole;
    }
    if (this.adminSecret) {
      headers['X-Admin-Secret'] = this.adminSecret;
    }
    return headers;
  }

  // Catalog
  async getOperators(): Promise<Operator[]> {
    const res = await fetch(`${API_BASE}/catalog/operators`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async getPackages(operatorId?: string): Promise<PackageItem[]> {
    const url = operatorId
      ? `${API_BASE}/catalog/packages?operator=${operatorId}`
      : `${API_BASE}/catalog/packages`;
    const res = await fetch(url, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async getAirtimeDenominations(operatorId?: string): Promise<AirtimeDenomination[]> {
    const url = operatorId
      ? `${API_BASE}/catalog/airtime-denominations?operator=${operatorId}`
      : `${API_BASE}/catalog/airtime-denominations`;
    const res = await fetch(url, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async getReceivingAccounts(): Promise<ReceivingAccount[]> {
    const res = await fetch(`${API_BASE}/catalog/receiving-accounts`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  // Wallet & Customer
  async getWallet(): Promise<{ wallet: Wallet; transactions: any[] }> {
    const res = await fetch(`${API_BASE}/customer/wallet`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data;
  }

  async createOrder(params: {
    operatorId: string;
    packageId?: string;
    orderType: 'package' | 'phone_bill';
    recipientPhone: string;
    amount: number;
    airtimeAmount?: number;
    paymentMethod: 'wallet' | 'direct_kpay' | 'direct_wave';
    txRef?: string;
    slipUrl?: string;
  }): Promise<Order> {
    const res = await fetch(`${API_BASE}/customer/orders`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(params),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Order placement failed');
    return data.data;
  }

  async getOrders(): Promise<Order[]> {
    const res = await fetch(`${API_BASE}/customer/orders`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async submitOrderSlip(orderId: string, txRef: string, slipUrl: string): Promise<Order> {
    const res = await fetch(`${API_BASE}/customer/orders/${orderId}/slip`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ txRef, slipUrl }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Slip submission failed');
    return data.data;
  }

  async createDeposit(amount: number, paymentMethod: string, txRef?: string, slipUrl?: string): Promise<Deposit> {
    const res = await fetch(`${API_BASE}/customer/deposits`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ amount, paymentMethod, txRef, slipUrl }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Deposit creation failed');
    return data.data;
  }

  async getDeposits(): Promise<Deposit[]> {
    const res = await fetch(`${API_BASE}/customer/deposits`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async getBuybackQuote(operator: string, amount: number) {
    const res = await fetch(
      `${API_BASE}/customer/buyback/quote?operator=${operator}&amount=${amount}`,
      { headers: this.getHeaders() }
    );
    const data = await res.json();
    return data.data;
  }

  async createBuyback(params: {
    operatorId: string;
    airtimeAmount: number;
    settlementType: string;
    senderPhone: string;
    payoutDestination?: string;
  }): Promise<any> {
    const res = await fetch(`${API_BASE}/customer/buyback`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(params),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Buyback request failed');
    return data.data;
  }

  async getBuybacks(): Promise<Buyback[]> {
    const res = await fetch(`${API_BASE}/customer/buybacks`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  // Admin APIs
  async getAdminQueue(): Promise<{ deposits: Deposit[]; orders: Order[]; buybacks: Buyback[] }> {
    const res = await fetch(`${API_BASE}/admin/queue`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data;
  }

  async updateOperatorPackageStatus(
    id: string,
    status: string,
    reason?: string,
    reopenAt?: string
  ): Promise<Operator> {
    const res = await fetch(`${API_BASE}/admin/operators/${id}/package-status`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ status, reason, reopenAt }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update operator status');
    return data.data;
  }

  async claimOrder(orderId: string): Promise<Order> {
    const res = await fetch(`${API_BASE}/admin/orders/${orderId}/claim`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Claim failed');
    return data.data;
  }

  async confirmDelivered(orderId: string, deliveryRef: string, deliveryNote?: string): Promise<Order> {
    const res = await fetch(`${API_BASE}/admin/orders/${orderId}/confirm-delivered`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ deliveryRef, deliveryNote }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Delivery confirmation failed');
    return data.data;
  }

  async approveDeposit(depositId: string): Promise<Deposit> {
    const res = await fetch(`${API_BASE}/admin/deposits/${depositId}/approve`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Deposit approval failed');
    return data.data;
  }

  async rejectDeposit(depositId: string, reason: string): Promise<Deposit> {
    const res = await fetch(`${API_BASE}/admin/deposits/${depositId}/reject`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ reason }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Deposit rejection failed');
    return data.data;
  }

  async settleBuyback(buybackId: string, payoutTxRef?: string): Promise<Buyback> {
    const res = await fetch(`${API_BASE}/admin/buybacks/${buybackId}/settle`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ payoutTxRef }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Buyback settlement failed');
    return data.data;
  }

  async getAdminPackages(operatorId?: string): Promise<PackageItem[]> {
    const url = operatorId ? `${API_BASE}/admin/packages?operator=${operatorId}` : `${API_BASE}/admin/packages`;
    const res = await fetch(url, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async togglePackageStock(packageId: string): Promise<PackageItem> {
    const res = await fetch(`${API_BASE}/admin/packages/${packageId}/toggle`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to toggle package stock');
    return data.data;
  }

  async updatePackage(packageId: string, updates: Partial<PackageItem>): Promise<PackageItem> {
    const res = await fetch(`${API_BASE}/admin/packages/${packageId}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update package');
    return data.data;
  }

  async createPackage(newPkg: Partial<PackageItem>): Promise<PackageItem> {
    const res = await fetch(`${API_BASE}/admin/packages`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(newPkg),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create package');
    return data.data;
  }

  async getAdminDashboard(filter?: DashboardFilter | DashboardTimeframe): Promise<DashboardStats> {
    let url = `${API_BASE}/admin/dashboard`;
    const params = new URLSearchParams();
    if (typeof filter === 'object' && filter !== null) {
      if (filter.days !== undefined) params.append('days', String(filter.days));
      if (filter.startDate) params.append('startDate', filter.startDate);
      if (filter.endDate) params.append('endDate', filter.endDate);
    } else if (filter !== undefined) {
      params.append('days', String(filter));
    } else {
      params.append('days', '30');
    }
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const res = await fetch(url, {
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load dashboard metrics');
    return data.data;
  }

  async getRates(): Promise<RateSchedule[]> {
    const res = await fetch(`${API_BASE}/catalog/rates`, { headers: this.getHeaders() });
    const data = await res.json();
    return data.data || [];
  }

  async updateAirtimeRate(
    operatorId: string,
    ratePercentage: number,
    direction: 'buy' | 'sell' = 'buy'
  ): Promise<RateSchedule> {
    const res = await fetch(`${API_BASE}/admin/rates`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ operatorId, ratePercentage, direction }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update airtime rate');
    return data.data;
  }

  async getAdminCustomers(params?: { search?: string; sort?: string; limit?: number; banned?: boolean }): Promise<AdminCustomerListResult> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('q', params.search);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.banned) searchParams.append('banned', 'true');

    const url = `${API_BASE}/admin/customers${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const res = await fetch(url, { headers: this.getHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load customers');
    return data.data;
  }

  async getAdminCustomerDetail(id: string): Promise<AdminCustomerDetailResult> {
    const res = await fetch(`${API_BASE}/admin/customers/${id}`, { headers: this.getHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load customer details');
    return data.data;
  }

  async banCustomer(id: string, reason?: string): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/customers/${id}/ban`, {
      method: 'POST',
      headers: { ...this.getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to ban customer');
    return data.data;
  }

  async unbanCustomer(id: string): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/customers/${id}/unban`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to unban customer');
    return data.data;
  }

  async getAdminAirtimeDenominations(): Promise<AirtimeDenomination[]> {
    const res = await fetch(`${API_BASE}/admin/airtime-denominations`, { headers: this.getHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load airtime denominations');
    return data.data || [];
  }

  async saveAirtimeDenomination(denomination: Partial<AirtimeDenomination>): Promise<AirtimeDenomination> {
    const res = await fetch(`${API_BASE}/admin/airtime-denominations`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(denomination),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to save airtime denomination');
    return data.data;
  }

  async toggleAirtimeDenomination(id: string): Promise<AirtimeDenomination> {
    const res = await fetch(`${API_BASE}/admin/airtime-denominations/${id}/toggle`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to toggle airtime denomination');
    return data.data;
  }

  async deleteAirtimeDenomination(id: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/admin/airtime-denominations/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to delete airtime denomination');
    return data;
  }

  async batchUpdateAirtimeRates(
    operatorId: string,
    ratePercentage: number,
    costPercentage?: number
  ): Promise<AirtimeDenomination[]> {
    const res = await fetch(`${API_BASE}/admin/airtime-denominations/batch-rate`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ operatorId, ratePercentage, costPercentage }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to batch update airtime rates');
    return data.data || [];
  }
}

export const api = new ApiClient();
